import { useEffect, useState } from "react";
import { Upload, X, FileText, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, upload, fileUrl, errMsg, fmtDateTime } from "../lib/api";

export const C_STATUS = {
  submitted: ["Submitted", "bg-sky-50 text-sky-700"], under_review: ["Under review", "bg-amber-50 text-amber-700"],
  info_requested: ["More info needed", "bg-violet-50 text-violet-700"], resolved: ["Resolved", "bg-emerald-50 text-emerald-700"],
  appealed: ["Review requested", "bg-rose-50 text-rose-700"], closed: ["Closed", "bg-slate-100 text-slate-600"], withdrawn: ["Withdrawn", "bg-slate-100 text-slate-500"],
};

export const CStatus = ({ s }) => <span className={`chip ${C_STATUS[s]?.[1] || "bg-slate-100"}`} data-testid={`complaint-status-${s}`}>{C_STATUS[s]?.[0] || s}</span>;

export function useComplaintOptions() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/complaints/options").then((r) => setO(r.data)).catch(() => {}); }, []);
  return o;
}

export function EvidenceUploader({ value, onChange, max = 10, testid = "evidence-upload" }) {
  const [busy, setBusy] = useState(false);
  const add = async (files) => {
    const list = Array.from(files).slice(0, max - value.length);
    if (!list.length) return toast.error(`You can upload up to ${max} files`);
    setBusy(true);
    try {
      const out = [];
      for (const f of list) out.push(await upload(f));
      onChange([...value, ...out]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-sm text-slate-500 cursor-pointer hover:border-[#FF8166] transition-colors" data-testid={testid}>
        {busy ? <Loader2 className="animate-spin" /> : <Upload />}
        <span>{busy ? "Uploading…" : "Add photos, videos or PDFs (max 15MB each)"}</span>
        <input type="file" multiple accept="image/*,video/mp4,video/quicktime,application/pdf" className="hidden" disabled={busy} onChange={(e) => add(e.target.files)} data-testid={`${testid}-input`} />
      </label>
      <EvidenceList items={value} onRemove={(i) => onChange(value.filter((_, j) => j !== i))} />
    </div>
  );
}

export function EvidenceList({ items, onRemove }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3" data-testid="evidence-list">{items.map((f, i) => (
      <div key={f.url} className="relative group">
        <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="block w-24 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
          {f.type === "image" ? <img src={fileUrl(f.url)} alt={f.name} className="w-full h-full object-cover" /> :
            <span className="w-full h-full flex flex-col items-center justify-center text-[10px] text-slate-500 p-1 text-center">{f.type === "video" ? <Film size={18} /> : <FileText size={18} />}<span className="truncate w-full">{f.name}</span></span>}
        </a>
        {onRemove && <button type="button" onClick={() => onRemove(i)} className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5 shadow" aria-label="Remove file" data-testid={`evidence-remove-${i}`}><X size={12} /></button>}
      </div>))}
    </div>
  );
}

export const Timeline = ({ items }) => (
  <ol className="space-y-2 text-xs" data-testid="complaint-timeline">{(items || []).map((t, i) => (
    <li key={i} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#FF8166] mt-1.5 shrink-0" /><span><b className="text-slate-700">{t.event}</b><br /><span className="text-slate-500">{t.by} · {fmtDateTime(t.at)}</span></span></li>))}</ol>
);

export const Section = ({ n, title, hint, children, testid }) => (
  <section className="card p-5 sm:p-6 fade-up" data-testid={testid}>
    <div className="flex items-start gap-3 mb-4"><span className="w-8 h-8 shrink-0 rounded-full bg-[#374B5C] text-white grid place-items-center text-sm font-bold">{n}</span>
      <div><h2 className="font-bold text-slate-900">{title}</h2>{hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}</div></div>
    <div className="space-y-4">{children}</div>
  </section>
);
